// CPF mask, normalization and checksum validation. Pure, shared by client and server.

import { z } from "zod";

import { onlyDigits } from "@/lib/strings";

export function maskCpf(rawValue: string): string {
  const digits = onlyDigits(rawValue).slice(0, 11);
  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 6);
  const part3 = digits.slice(6, 9);
  const part4 = digits.slice(9, 11);

  let masked = part1;
  if (part2) masked += `.${part2}`;
  if (part3) masked += `.${part3}`;
  if (part4) masked += `-${part4}`;
  return masked;
}

function checkDigit(digits: string, length: number): number {
  let sum = 0;
  for (let i = 0; i < length; i++) {
    sum += Number(digits[i]) * (length + 1 - i);
  }
  const remainder = (sum * 10) % 11;
  return remainder === 10 ? 0 : remainder;
}

/** Real mod-11 checksum, not just a length/format check. */
export function isValidCpf(digits: string): boolean {
  if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) return false;
  return checkDigit(digits, 9) === Number(digits[9]) && checkDigit(digits, 10) === Number(digits[10]);
}

/** Normalizes to digits and validates length + checksum at the BFF boundary. */
export const taxPayerIdSchema = z
  .string()
  .transform(onlyDigits)
  .pipe(
    z.string()
      .length(11, "CPF must have 11 digits.")
      .refine(isValidCpf, "Invalid CPF checksum."),
  );
